import React from 'react';
import moment from 'moment';
import { FormattedMessage } from 'react-intl';
import { round } from 'lodash';

import {
  REQUEST_EMPLOYEE_MASTER_COURSES,
  REQUEST_EMPLOYEE_MASTER_COURSES_FAILURE,
  REQUEST_EMPLOYEE_MASTER_COURSES_SUCCESS,
  REQUEST_COURSES,
  REQUEST_COURSES_FAILURE,
  REQUEST_COURSES_SUCCESS,
  REQUEST_COURSE_ATTENDANCE,
  REQUEST_COURSE_ATTENDANCE_SUCCESS,
  REQUEST_COURSE_ATTENDANCE_FAILURE,
  REQUEST_COURSE_LEAVE,
  REQUEST_COURSE_LEAVE_SUCCESS,
  REQUEST_COURSE_LEAVE_FAILURE,
} from '../constants/ActionTypes';

import { STRING, DATE, NUMBER, BOOL, TABLE, TIME, HTML } from '../constants/DataTypes';
import { fields, detailFields, entityMap } from '../constants/fields/EmployeeMasterCourse';
import convert, { convertTime, duration } from '../utils/dateTools';

export function mapCourseTimes(ct) {
  return {
    fields: [
      'CourseTimeDate',
      'CourseTimeBegti',
      'CourseTimeEndti',
      'HrLocationName',
      'HrRoomName',
    ],
    CourseTimeDate: {
      type: DATE,
      value: convert(ct.CourseTimeDate),
      label: <div><FormattedMessage id="courses.date" /></div>,
      style: {
        width: 92,
      },
    },
    CourseTimeBegti: {
      type: TIME,
      value: convertTime(ct.CourseTimeBegti),
      label: <div><FormattedMessage id="courses.start" /></div>,
      style: {
        width: 64,
      },
    },
    CourseTimeEndti: {
      type: TIME,
      value: convertTime(ct.CourseTimeEndti),
      label: <div><FormattedMessage id="courses.end" /></div>,
      style: {
        width: 64,
      },
    },
    HrLocationName: {
      type: STRING,
      value: ct.HrLocationName,
      label: <div><FormattedMessage id="courses.location" /></div>,
      style: {
        width: 100,
      },
    },
    HrRoomName: {
      type: STRING,
      value: ct.HrRoomName,
      label: <div><FormattedMessage id="courses.room" /></div>,
      style: {
        width: 100,
      },
    },
  };
}

function renderTimeDuration(hours) {
  return (
    <FormattedMessage
      id="courses.duration.value"
      values={{
        hours: Math.floor(moment.duration(hours, 'hours').asMinutes() / 60),
        minutes: round(moment.duration(hours, 'hours').asMinutes() % 60),
      }}
    />
  );
}

function mapCourse(c) {
  const price = Math.ceil(c.CourseCost / c.CourseMaxAttendance);
  return {
    fields: [
      'CourseName',
      'CourseStatusPlannedName',
      'CourseBegda',
      'CourseEndda',
      'CourseHours',
      'AttendingName',
    ],
    detailFields: [
      'CourseName',
      'CourseStatusPlannedName',
      'CourseBegda',
      'CourseEndda',
      'CourseHours',
      'AttendingName',
      'CourseTypeName',
      'CourseGroupName',
      'CourseInstructor',
      'CourseInstructorEmployeeName',
      'CourseDescriptionHtml',
      'CourseTimes',
      'CourseCost',
      'CourseDeadlineStart',
      'CourseDeadlineEnd',
    ],
    CourseInstructor: {
      label: <div><FormattedMessage id="courses.instructor" /></div>,
      type: STRING,
      value: c.CourseInstructor,
    },
    CourseInstructorEmployeeName: {
      label: <div><FormattedMessage id="courses.instructor" /></div>,
      type: STRING,
      value: c.CourseInstructorEmployeeName,
    },
    CourseTypeName: {
      label: <div><FormattedMessage id="courses.type" /></div>,
      type: STRING,
      value: c.CourseTypeName,
      entityValue: c.HrCourseType,
    },
    CourseGroupName: {
      label: <div><FormattedMessage id="courses.group" /></div>,
      type: STRING,
      value: c.CourseGroupName,
      entityValue: c.HrCourseGroupID,
    },
    CourseDescriptionHtml: {
      label: <div><FormattedMessage id="courses.description" /></div>,
      type: HTML,
      value: c.CourseDescriptionHtml || c.CourseDescription,
    },
    CourseTimes: {
      label: <div><FormattedMessage id="courses.times" /></div>,
      type: TABLE,
      value: c.CourseTimes.map(mapCourseTimes),
    },
    CourseID: {
      type: NUMBER,
      value: c.CourseID,
    },
    CourseAttendeeID: {
      value: c.MainCourseAttendeeID,
      type: NUMBER,
    },
    CourseAttendingAvailable: {
      value: c.CourseAttendeeRegistered < c.CourseMaxAttendance,
      type: BOOL,
    },
    CourseName: {
      label: <div><FormattedMessage id="courses.name" /></div>,
      value: c.CourseName,
      type: STRING,
      style: { width: 135 },
    },
    AttendingName: {
      label: <div><FormattedMessage id="courses.registered" /></div>,
      value: `${c.CourseAttendeeRegistered}/${c.CourseMaxAttendance}`,
      type: STRING,
      style: { width: 75 },
    },
    CourseStatusPlannedName: {
      label: <div><FormattedMessage id="courses.status" /></div>,
      value: c.CourseStatusPlannedName,
      type: STRING,
      style: { width: 70 },
    },
    CourseHours: {
      label: <div><FormattedMessage id="courses.length" /></div>,
      value: renderTimeDuration(c.CourseHours),
      type: STRING,
      style: { width: 95 },
    },
    CourseBegda: {
      label: <div><FormattedMessage id="courses.start" /></div>,
      value: convert(c.CourseBegda),
      entityValue: c.CourseBegda,
      type: DATE,
      style: { width: 70 },
    },
    CourseEndda: {
      label: <div><FormattedMessage id="courses.end" /></div>,
      value: convert(c.CourseEndda),
      type: DATE,
      style: { width: 75 },
    },
    CourseCost: {
      label: <div><FormattedMessage id="courses.price" /></div>,
      type: STRING,
      value: <FormattedMessage id="courses.pricePerAttendee" values={{ price, attendees: c.CourseMaxAttendance }} />,
      style: { width: 70 },
    },
    CourseDeadlineStart: {
      label: <div><FormattedMessage id="courses.CourseDeadlineStart" /></div>,
      type: NUMBER,
      value: <span>{c.CourseDeadlineStart} <FormattedMessage id="hours" values={{ val: c.CourseDeadlineStart }} /></span>,
      style: { width: 70 },
    },
    CourseDeadlineEnd: {
      label: <div><FormattedMessage id="courses.CourseDeadlineEnd" /></div>,
      type: NUMBER,
      value: <span>{c.CourseDeadlineEnd} <FormattedMessage id="hours" values={{ val: c.CourseDeadlineEnd }} /></span>,
      style: { width: 70 },
    },
    entity: c.Entity,
  };
}

export function mapEmplyeeCourses(c) {
  return {
    fields,
    detailFields,
    entityMap,
    Entity: c.Entity,
    HrRoomName: {
      label: <div><FormattedMessage id="course.room" /></div>,
      value: c.HrRoomName,
      Type: STRING,
      entity: 'HrRoomID',
    },
    CourseDescription: {
      label: <div><FormattedMessage id="course.description" /></div>,
      value: c.CourseDescription,
      type: STRING,
      entity: 'CourseDescription',
    },
    Comments: {
      label: <div><FormattedMessage id="course.comments" /></div>,
      value: c.Comments,
      type: STRING,
      entity: 'Comments',
    },
    CourseName: {
      label: <div><FormattedMessage id="course.name" /></div>,
      value: c.CourseName,
      type: STRING,
      entity: 'CourseName',
    },
    CourseTypeName: {
      label: <div><FormattedMessage id="course.type" /></div>,
      value: c.CourseTypeName,
      type: STRING,
      entity: 'CourseType',
    },
    CourseGroupName: {
      label: <div><FormattedMessage id="course.group" /></div>,
      value: c.CourseGroupName,
      type: STRING,
      entity: 'CourseGroupID',
    },
    CourseHrLocationName: {
      label: <div><FormattedMessage id="course.location" /></div>,
      value: c.CourseHrLocationName,
      type: STRING,
      entity: 'HrLocationID',
    },
    CourseLength: {
      label: <div><FormattedMessage id="course.length" /></div>,
      value: duration(c.CourseLength),
      type: STRING,
      entity: 'CourseLength',
    },
    CoursePoints: {
      label: <div><FormattedMessage id="course.points" /></div>,
      value: c.CoursePoints,
      type: NUMBER,
      entity: 'CoursePoints',
    },
    CoursePeriodBegda: {
      label: <div><FormattedMessage id="course.start" /></div>,
      value: convert(c.CoursePeriodBegda),
      type: DATE,
      entity: 'CoursePeriodBegda',
    },
    CoursePeriodEnda: {
      label: <div><FormattedMessage id="course.end" /></div>,
      value: convert(c.CoursePeriodEnda),
      type: DATE,
      entity: 'CoursePeriodEnda',
    },
  };
}

export default function coursesReducer(state = { coursesLoaded: false }, action) {
  switch (action.type) {
    case REQUEST_EMPLOYEE_MASTER_COURSES: {
      return {
        ...state,
        ...action,
      };
    }
    case REQUEST_EMPLOYEE_MASTER_COURSES_FAILURE: {
      return {
        ...state,
        ...action,
      };
    }
    case REQUEST_EMPLOYEE_MASTER_COURSES_SUCCESS: {
      return {
        ...state,
        ...action,
        employeeMasterCoursesData: action.employeeMasterCourses.map(mapEmplyeeCourses),
      };
    }
    /* *******************COURSES********************************* */
    case REQUEST_COURSES: {
      return {
        ...state,
        coursesLoaded: false,
        courses: [],
        sentAt: Date.now(),
        error: null,
      };
    }
    case REQUEST_COURSES_FAILURE: {
      return {
        ...state,
        coursesLoaded: true,
        courses: [],
        receivedAt: Date.now(),
        coursesError: action.coursesError,
      };
    }
    case REQUEST_COURSES_SUCCESS: {
      return {
        ...state,
        coursesLoaded: true,
        receivedAt: Date.now(),
        error: null,
        allCourses: action.allCourses,
        attendingCourses: action.attendingCourses.map(mapCourse),
        openCourses: action.openCourses.map(mapCourse),
      };
    }
    case REQUEST_COURSE_ATTENDANCE:
      return {
        ...state,
      };
    case REQUEST_COURSE_ATTENDANCE_SUCCESS:
      return {
        ...state,
      };
    case REQUEST_COURSE_ATTENDANCE_FAILURE:
      return {
        ...state,
        coursesError: action.error,
      };
    case REQUEST_COURSE_LEAVE:
      return {
        ...state,
      };
    case REQUEST_COURSE_LEAVE_SUCCESS:
      return {
        ...state,
      };
    case REQUEST_COURSE_LEAVE_FAILURE:
      return {
        ...state,
        coursesError: action.error,
      };
    default:
      return state;
  }
}



// WEBPACK FOOTER //
// ./src/reducers/coursesReducer.js